// First-person viewmodel: its own scene and camera (rendered after the world with depth cleared),
// procedural weapon animation and the FOV-corrected muzzle position.
//
// Conventions: the viewmodel camera sits at the origin looking down -Z, so scene coordinates are
// camera-space metres. A weapon is placed by pinning a pivot point of the weapon (the right-hand
// grip while hip-firing, the sight anchor while aiming) to a camera-space point and rotating about
// it; every animation is a PoseDelta applied on top.

import * as THREE from "three";
import { createWeaponModel } from "./models/weapons";
import { attachmentSlot, withDefaultOptic } from "./models/attachments";
import type { WeaponModel } from "./models/weapons";
import { createViewmodelHands } from "./models/hands";
import type { ViewmodelHands } from "./models/hands";
import { viewmodelVerticalFovDeg } from "./cameraMath";
import {
  PoseDelta,
  Spring,
  clamp01,
  ease,
  handCarryingMagazine,
  handShellLoading,
  handToMagazine,
  lowerPose,
  magazineState,
  meleePose,
  raisePose,
  reloadKnock,
  reloadTilt,
  throwPose,
} from "./viewmodelMath";
import type { Vec3Like, ViewmodelRenderer, ViewmodelState } from "./types";

const DEG = Math.PI / 180;
/**
 * Distance from the eye to the sight anchor while aiming (m) and the scale of the gun at full ADS.
 * Both push the gun out of the way: 0.44 m and 0.7 for iron sights (the sight picture has to stay
 * readable), 0.62 m and 0.5 behind an optic. The forearms are tucked to a stub (`setTuck`).
 */
const EYE_RELIEF_IRON = 0.44;
const EYE_RELIEF_OPTIC = 0.62;
const ADS_SCALE_IRON = 0.7;
const ADS_SCALE_OPTIC = 0.5;
/** Camera-space position of the right-hand grip at the hip. */
const HIP_GRIP = new THREE.Vector3(0.18, -0.23, -0.55);
const KNIFE_GRIP = new THREE.Vector3(0.2, -0.2, -0.5);
const HIP_YAW = 0.035;
const LOWER_TIME = 0.14;
const RAISE_TIME = 0.28;
const MAX_CACHED = 4;
const RELOAD_POSE = new PoseDelta().set(-0.13, -0.05, 0.06, 0.3, 0.2, -0.7);

const _p = new THREE.Vector3();
const _pcam = new THREE.Vector3();
const _v = new THREE.Vector3();
const _m = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const AXIS_X = new THREE.Vector3(1, 0, 0);

interface Held {
  key: string;
  id: string;
  model: WeaponModel;
  hands: ViewmodelHands;
  /** Camera-space transform of the weapon. */
  pose: THREE.Group;
  grip: THREE.Vector3;
  sight: THREE.Vector3;
  /** Camera-space position of the sight anchor at full ADS and the scale of the gun there. */
  adsCam: THREE.Vector3;
  adsScale: number;
  magBase: THREE.Vector3 | null;
  magGrip: THREE.Vector3;
  lastUse: number;
  /** 0..1 while being lowered after a switch, -1 otherwise. */
  lowerT: number;
  basePos: THREE.Vector3;
  baseQuat: THREE.Quaternion;
}

export class Viewmodel implements ViewmodelRenderer {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(50, 16 / 9, 0.01, 8);
  /** Muzzle flash light on the gun (always present so the shader program stays stable). */
  readonly flashLight = new THREE.PointLight(0xffc880, 0, 2.5, 2);

  private readonly sun = new THREE.DirectionalLight(0xffffff, 1);
  private readonly hemi = new THREE.HemisphereLight(0xffffff, 0x888888, 1);
  private readonly worldCamera: THREE.PerspectiveCamera;
  private readonly cache = new Map<string, Held>();
  private current: Held | null = null;
  private previous: Held | null = null;
  private knifeHeld: Held | null = null;
  private displayed: Held | null = null;
  private curId = "";
  private curCamo = "";
  private curAtt: string[] = [];
  private enabled = true;
  private shownNow = false;
  private scope = false;
  private knifeShown = false;
  private useCounter = 0;

  private time = 0;
  private phase = 0;
  private moveAmt = 0;
  private air = 0;
  private slide = 0;
  private wall = 0;
  private wasGround = true;
  private raiseT = 1;
  private rollSide = 1;
  private rng = 0x9e3779b9;
  private tanX = 1;
  private tanY = 1;

  private readonly swayX = new Spring(11, 0.75);
  private readonly swayY = new Spring(11, 0.75);
  private readonly kickZ = new Spring(26, 0.55);
  private readonly kickPitch = new Spring(26, 0.55);
  private readonly kickX = new Spring(26, 0.55);
  private readonly kickYaw = new Spring(22, 0.6);
  private readonly kickRoll = new Spring(22, 0.6);
  private readonly land = new Spring(15, 0.6);
  private readonly delta = new PoseDelta();
  private readonly tmp = new PoseDelta();
  private readonly mag = { drop: 0, visible: true };

  constructor(worldCamera: THREE.PerspectiveCamera) {
    this.worldCamera = worldCamera;
    this.scene.add(this.camera, this.sun, this.hemi, this.flashLight);
    this.camera.updateMatrixWorld(true);
    this.setProjection(16 / 9, 68);
  }

  /** viewmodelFov: settings.video.viewmodelFov (horizontal FOV at 4:3). */
  setProjection(aspect: number, viewmodelFov: number): void {
    const vfov = viewmodelVerticalFovDeg(viewmodelFov);
    this.camera.fov = vfov;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.tanY = Math.tan((vfov * DEG) / 2);
    this.tanX = this.tanY * aspect;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** Copies the world's light colours so the weapon matches the map; `env` is the shared sky environment. */
  setLighting(sun: THREE.DirectionalLight, hemi: THREE.HemisphereLight, env: THREE.Texture | null, envIntensity: number): void {
    this.sun.color.copy(sun.color);
    this.sun.intensity = sun.intensity * 0.8;
    this.hemi.color.copy(hemi.color);
    this.hemi.groundColor.copy(hemi.groundColor);
    this.hemi.intensity = hemi.intensity;
    this.scene.environment = env;
    this.scene.environmentIntensity = envIntensity;
  }

  /** Aims the viewmodel sun along the world sun direction as seen from the world camera. */
  setSunDirection(sunDirWorld: THREE.Vector3): void {
    _q.copy(this.worldCamera.quaternion).invert();
    this.sun.position.copy(sunDirWorld).applyQuaternion(_q).multiplyScalar(10);
  }

  /** True when the viewmodel scene has something to draw. */
  get active(): boolean {
    return this.shownNow && this.displayed !== null && !this.scope;
  }

  scopeActive(): boolean {
    return this.scope;
  }

  setLoadoutWeapon(id: string, attachments: string[], camo: string): void {
    this.setWeapon(id, attachments, camo);
  }

  update(s: ViewmodelState, dtIn: number): void {
    const dt = Math.min(Math.max(dtIn, 0), 0.1);
    this.time += dt;
    this.tickPrevious(dt);

    if (!s.visible || !this.enabled) {
      this.shownNow = false;
      this.scope = false;
      this.displayed = null;
      this.hideAll();
      return;
    }

    // Loadout identity (allocation-free compare).
    let same = s.weaponId === this.curId && s.camo === this.curCamo && s.attachments.length === this.curAtt.length;
    for (let i = 0; same && i < s.attachments.length; i++) same = s.attachments[i] === this.curAtt[i];
    if (!same) this.setWeapon(s.weaponId, s.attachments, s.camo);

    const meleeActive = s.meleeFrac > 0 && s.meleeFrac < 1;
    const useKnife = meleeActive && s.weaponId !== "knife";
    if (useKnife && this.knifeHeld === null) this.knifeHeld = this.acquire("knife", [], "none");
    const main = useKnife ? this.knifeHeld : this.current;
    if (!useKnife && this.knifeShown) this.raiseT = 0; // the gun comes back up after the quick melee
    this.knifeShown = useKnife;
    if (main === null) {
      this.shownNow = false;
      this.displayed = null;
      this.hideAll();
      return;
    }
    if (this.current !== null && this.current !== main) this.current.pose.visible = false;
    if (this.knifeHeld !== null && this.knifeHeld !== main) this.knifeHeld.pose.visible = false;

    main.lastUse = ++this.useCounter;
    const ads = useKnife ? 0 : ease(s.adsFrac);
    this.scope =
      !useKnife && main.model.hasScopeOverlay && (this.scope ? s.adsFrac >= 0.95 : s.adsFrac >= 0.985);
    if (this.raiseT < 1) this.raiseT = Math.min(1, this.raiseT + dt / RAISE_TIME);
    const raise = useKnife ? 1 : s.switchFrac >= 0 ? clamp01(s.switchFrac) : this.raiseT;

    this.animate(s, main, dt, ads, raise, useKnife, meleeActive);

    main.pose.visible = !this.scope;
    this.shownNow = true;
    this.displayed = main;
    this.scene.updateMatrixWorld(true);
  }

  /** Muzzle position in viewmodel scene space (camera space). False when no weapon is shown. */
  muzzleScenePosition(out: THREE.Vector3): boolean {
    if (this.displayed === null) return false;
    out.setFromMatrixPosition(this.displayed.model.muzzle.matrixWorld);
    return true;
  }

  /**
   * Ejection port in viewmodel scene space and the direction a case leaves it (right, up and a bit
   * back, in camera space). False when no weapon is shown.
   */
  ejectScenePose(pos: THREE.Vector3, dir: THREE.Vector3): boolean {
    const h = this.displayed;
    if (h === null) return false;
    const m = h.model;
    const reach = Math.min(0.22, Math.abs(m.muzzle.position.z) * 0.25);
    pos.set(0.03, m.muzzle.position.y - 0.012, -reach).applyMatrix4(m.root.matrixWorld);
    dir.set(1, 0.6, 0.35).transformDirection(m.root.matrixWorld);
    return true;
  }

  /**
   * World position that projects to the same screen point as the visible muzzle, at the same depth
   * (the viewmodel FOV differs from the world FOV, so the plain transform would be off).
   */
  muzzleWorldPosition(out: Vec3Like = { x: 0, y: 0, z: 0 }): Vec3Like {
    const cam = this.worldCamera;
    if (!this.muzzleScenePosition(_m)) {
      _m.set(0.14, -0.12, -0.7);
    } else {
      const depth = Math.max(-_m.z, 0.05);
      const nx = _m.x / (depth * this.tanX);
      const ny = _m.y / (depth * this.tanY);
      const wy = Math.tan((cam.fov * DEG) / 2);
      _m.set(nx * wy * cam.aspect * depth, ny * wy * depth, -depth);
    }
    _m.applyMatrix4(cam.matrixWorld);
    out.x = _m.x;
    out.y = _m.y;
    out.z = _m.z;
    return out;
  }

  private hideAll(): void {
    if (this.current !== null) this.current.pose.visible = false;
    if (this.knifeHeld !== null) this.knifeHeld.pose.visible = false;
  }

  private nextRandom(): number {
    this.rng = (Math.imul(this.rng, 1664525) + 1013904223) >>> 0;
    return this.rng / 4294967296;
  }

  private animate(
    s: ViewmodelState,
    h: Held,
    dt: number,
    ads: number,
    raise: number,
    isKnifeMelee: boolean,
    meleeActive: boolean,
  ): void {
    const k8 = 1 - Math.exp(-8 * dt);
    const k12 = 1 - Math.exp(-12 * dt);
    const d = this.delta.reset();
    const calm = 1 - 0.85 * ads;

    // Idle breathing.
    d.y += Math.sin(this.time * 1.6) * 0.0022 * calm;
    d.x += Math.sin(this.time * 0.9) * 0.0012 * calm;
    d.pitch += Math.sin(this.time * 1.35 + 1) * 0.003 * calm;

    // Movement bob, scaled by ground speed.
    const moving = s.onGround && !s.sliding ? Math.min(s.speed / 7.6, 1.4) : 0;
    this.moveAmt += (moving - this.moveAmt) * k8;
    this.phase += dt * (1.4 + 0.16 * s.speed) * (this.moveAmt > 0.02 ? 1 : 0);
    const bob = this.moveAmt * calm;
    const c = this.phase * Math.PI * 2;
    d.x += Math.sin(c) * 0.011 * bob;
    d.y += Math.sin(c * 2) * 0.008 * bob;
    d.z += Math.cos(c * 2) * 0.004 * bob;
    d.roll += Math.sin(c) * 0.014 * bob;

    // Air, landing, slide, wall run.
    this.air += ((s.onGround ? 0 : 1) - this.air) * k8;
    if (s.onGround && !this.wasGround) this.land.v -= 0.7;
    this.wasGround = s.onGround;
    this.land.step(0, dt);
    d.y += this.air * 0.012 * calm + this.land.x * calm;
    d.pitch += this.air * 0.04 * calm;
    this.slide += ((s.sliding ? 1 : 0) - this.slide) * k12;
    d.x -= 0.04 * this.slide;
    d.y -= 0.05 * this.slide;
    d.z += 0.03 * this.slide;
    d.roll -= 0.2 * this.slide;
    d.pitch += 0.05 * this.slide;
    this.wall += (s.wallrunSide - this.wall) * k8;
    d.x -= 0.03 * this.wall;
    d.roll -= 0.08 * this.wall;

    // Mouse sway: the gun lags behind the view.
    const lx = Math.max(-30, Math.min(30, s.lookDeltaX));
    const ly = Math.max(-30, Math.min(30, s.lookDeltaY));
    this.swayX.v -= lx * 5.5;
    this.swayY.v -= ly * 4.5;
    this.swayX.step(0, dt);
    this.swayY.step(0, dt);
    const sx = Math.max(-3, Math.min(3, this.swayX.x)) * calm;
    const sy = Math.max(-3, Math.min(3, this.swayY.x)) * calm;
    d.x += sx * 0.01;
    d.y += sy * 0.008;
    d.yaw += sx * 0.018;
    d.pitch += sy * 0.014;

    // Recoil: every shot throws the gun back, up, sideways and into a roll, then under-damped
    // springs bring it home. The throw grows with the view kick of the shot (a revolver hits far
    // harder than a submachine gun) and shrinks while aiming. Several shots in one frame add up.
    if (s.shotsThisFrame > 0) {
      const heavy = Math.max(0.7, Math.min(0.6 + 0.45 * s.shotKickDeg, 3.2));
      const kick = Math.min(s.shotsThisFrame, 3) * heavy * (1 - 0.4 * ads);
      this.rollSide = -this.rollSide;
      this.kickZ.v += 2.2 * kick;
      this.kickPitch.v += 4.4 * kick;
      this.kickX.v += (this.nextRandom() - 0.5) * 0.5 * kick;
      this.kickYaw.v += (this.nextRandom() - 0.5) * 2 * kick;
      this.kickRoll.v += this.rollSide * (0.8 + this.nextRandom() * 0.8) * 3 * kick;
    }
    this.kickZ.step(0, dt);
    this.kickPitch.step(0, dt);
    this.kickX.step(0, dt);
    this.kickYaw.step(0, dt);
    this.kickRoll.step(0, dt);
    d.z += Math.min(this.kickZ.x, 0.1);
    d.x += Math.max(-0.03, Math.min(this.kickX.x, 0.03));
    d.pitch += Math.max(-0.2, Math.min(this.kickPitch.x, 0.2));
    d.yaw += this.kickYaw.x;
    d.roll += Math.max(-0.14, Math.min(this.kickRoll.x, 0.14));
    // The gun climbs a little further than the camera does.
    d.pitch += Math.max(-0.1, Math.min(s.kickPitchDeg * 0.4 * DEG, 0.1));
    d.yaw -= Math.max(-0.1, Math.min(s.kickYawDeg * 0.4 * DEG, 0.1));

    // Reload, switch, melee, throw.
    const reloading = s.reloadFrac >= 0 && s.reloadFrac <= 1 && !isKnifeMelee;
    if (reloading) {
      d.addScaled(RELOAD_POSE, reloadTilt(s.reloadFrac));
      const knock = reloadKnock(s.reloadFrac);
      d.z += 0.03 * knock;
      d.pitch += 0.1 * knock;
    }
    if (raise < 1) d.addScaled(raisePose(raise, this.tmp), 1);
    if (meleeActive) d.addScaled(meleePose(s.meleeFrac, this.tmp), 1);
    if (s.throwFrac > 0 && s.throwFrac < 1) d.addScaled(throwPose(s.throwFrac, this.tmp), 1);

    // Pin the pivot (grip -> sight while aiming) to its camera-space target and rotate about it.
    _p.lerpVectors(h.grip, h.sight, ads);
    _pcam.lerpVectors(h.id === "knife" ? KNIFE_GRIP : HIP_GRIP, h.adsCam, ads);
    _pcam.x += d.x;
    _pcam.y += d.y;
    _pcam.z += d.z;
    _e.set(d.pitch, d.yaw + HIP_YAW * (1 - ads), d.roll, "YXZ");
    _q.setFromEuler(_e);
    // The gun is drawn smaller while aiming (scaled about the sight, so the sight stays on the view axis).
    const scale = 1 + (h.adsScale - 1) * ads;
    _v.copy(_p).multiplyScalar(scale).applyQuaternion(_q);
    h.pose.position.copy(_pcam).sub(_v);
    h.pose.quaternion.copy(_q);
    h.pose.scale.setScalar(scale);
    h.hands.setTuck(ads);
    h.basePos.copy(h.pose.position);
    h.baseQuat.copy(_q);

    this.animateReloadParts(h, s.reloadFrac, reloading);
  }

  /** Magazine drop and support-hand motion during a reload (and their reset afterwards). */
  private animateReloadParts(h: Held, t: number, reloading: boolean): void {
    const left = h.hands.left;
    const rest = h.hands.leftRest;
    if (!reloading) {
      left.position.copy(rest);
      if (h.model.magazine !== null && h.magBase !== null) {
        h.model.magazine.position.copy(h.magBase);
        h.model.magazine.visible = true;
      }
      return;
    }
    if (h.model.magazine !== null && h.magBase !== null) {
      magazineState(t, this.mag);
      h.model.magazine.position.set(h.magBase.x, h.magBase.y - this.mag.drop, h.magBase.z);
      h.model.magazine.visible = this.mag.visible;
      const toMag = handToMagazine(t);
      const carry = handCarryingMagazine(t);
      left.position.copy(rest);
      _v.copy(h.magGrip).sub(rest).multiplyScalar(toMag);
      left.position.add(_v);
      left.position.y -= 0.2 * carry;
      left.position.z += 0.05 * carry;
    } else {
      const shell = handShellLoading(t);
      left.position.copy(rest);
      left.position.y -= 0.06 * shell;
      left.position.z += 0.03 * shell;
    }
  }

  /** Lowers the weapon that was put away and hides it when done. */
  private tickPrevious(dt: number): void {
    const p = this.previous;
    if (p === null) return;
    p.lowerT += dt / LOWER_TIME;
    if (p.lowerT >= 1) {
      p.pose.visible = false;
      p.lowerT = -1;
      this.previous = null;
      return;
    }
    lowerPose(p.lowerT, this.tmp);
    p.pose.position.copy(p.basePos);
    p.pose.position.y += this.tmp.y;
    p.pose.position.z += this.tmp.z;
    p.pose.quaternion.copy(p.baseQuat).premultiply(_q.setFromAxisAngle(AXIS_X, this.tmp.pitch));
    p.pose.updateMatrixWorld(true);
  }

  private setWeapon(id: string, attachments: readonly string[], camo: string): void {
    this.curId = id;
    this.curCamo = camo;
    this.curAtt = attachments.slice();
    const next = id === "" ? null : this.acquire(id, attachments, camo);
    if (next === this.current) return;
    if (this.previous !== null && this.previous !== next) this.previous.pose.visible = false;
    this.previous = null;
    const old = this.current;
    if (old !== null && old.pose.visible && this.shownNow) {
      old.lowerT = 0;
      this.previous = old;
    } else if (old !== null) {
      old.pose.visible = false;
    }
    if (next !== null && next.lowerT >= 0) next.lowerT = -1;
    this.current = next;
    this.raiseT = 0;
  }

  /** Cached model for a loadout, built on first use. Null (with a warning) for unknown weapon ids. */
  private acquire(id: string, attachments: readonly string[], camo: string): Held | null {
    const worn = withDefaultOptic(id, attachments);
    const hasOptic = worn.some((a) => attachmentSlot(a) === "optic");
    const key = `${id}|${worn.join(",")}|${camo}`;
    let h = this.cache.get(key);
    if (h === undefined) {
      let model: WeaponModel;
      try {
        model = createWeaponModel(id, worn, camo);
      } catch (err) {
        console.warn(`viewmodel: cannot build "${id}"`, err);
        return null;
      }
      const hands = createViewmodelHands();
      hands.attachTo(model);
      const pose = new THREE.Group();
      pose.name = `viewmodel:${id}`;
      pose.add(model.root);
      pose.visible = false;
      this.scene.add(pose);
      h = {
        key,
        id,
        model,
        hands,
        pose,
        grip: model.rightHandGrip.position.clone(),
        sight: model.sightAnchor.position.clone(),
        adsCam: new THREE.Vector3(0, 0, -(hasOptic ? EYE_RELIEF_OPTIC : EYE_RELIEF_IRON)),
        adsScale: hasOptic ? ADS_SCALE_OPTIC : ADS_SCALE_IRON,
        magBase: model.magazine === null ? null : model.magazine.position.clone(),
        magGrip: model.magazine === null ? new THREE.Vector3() : model.magazine.position.clone().add(_v.set(0, -0.07, 0)),
        lastUse: ++this.useCounter,
        lowerT: -1,
        basePos: new THREE.Vector3(),
        baseQuat: new THREE.Quaternion(),
      };
      this.cache.set(key, h);
      this.evict();
    }
    h.lastUse = ++this.useCounter;
    return h;
  }

  private evict(): void {
    while (this.cache.size > MAX_CACHED) {
      let victim: Held | null = null;
      for (const h of this.cache.values()) {
        if (h === this.current || h === this.previous || h === this.knifeHeld || h === this.displayed) continue;
        if (victim === null || h.lastUse < victim.lastUse) victim = h;
      }
      if (victim === null) return;
      this.cache.delete(victim.key);
      this.disposeHeld(victim);
    }
  }

  private disposeHeld(h: Held): void {
    h.hands.dispose();
    h.model.dispose();
    h.pose.removeFromParent();
  }

  dispose(): void {
    for (const h of this.cache.values()) this.disposeHeld(h);
    this.cache.clear();
    this.current = this.previous = this.knifeHeld = this.displayed = null;
  }
}
